import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Home,
  MapPin,
  Menu,
  Settings,
  ShoppingBag,
  Store,
  Ticket,
  Users,
  UserCheck,
  X
} from 'lucide-react';
import '../css/sidebar.css';

const Sidebar = ({ initialActivePage = '' }) => {
  const [activePage, setActivePage] = useState(initialActivePage);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const navigate = useNavigate(); // ✅ React Router navigation

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, href: '/' },
    {
      id: 'merchants',
      label: 'Merchants',
      icon: Store,
      subItems: [
        { id: 'attractions', label: 'Attractions', href: '/CompactMerchantManagementPage?type=Attractions' },
        { id: 'taxi', label: 'Taxi', href: '/CompactMerchantManagementPage?type=Taxi' },
        { id: 'train', label: 'Train', href: '/CompactMerchantManagementPage?type=Train' },
        { id: 'hotel', label: 'Hotel', href: '/CompactMerchantManagementPage?type=Hotel' },
        { id: 'tour-operator', label: 'Tour Operator', href: '/CompactMerchantManagementPage?type=Tour%20Operator' },
        { id: 'tour-guide', label: 'Tour Guide', href: '/CompactMerchantManagementPage?type=Tour%20Guide' },
      ]
    },
    {
      id: 'sources',
      label: 'Sources',
      icon: MapPin,
      subItems: [
        { id: 'destinations', label: 'Destinations', href: '/DestinationList?type=Destinations' },
        { id: 'hotels', label: 'Hotels', href: '/DestinationList?type=Hotels' },
        { id: 'trains', label: 'Trains', href: '/DestinationList?type=Trains' },
      ]
    },
    {
      id: 'booking-types',
      label: 'Booking Types',
      icon: Ticket,
      subItems: [
        { id: 'tickets', label: 'Tickets', href: '/TicketManagement' },
        { id: 'rooms', label: 'Rooms', href: '/DestinationList?type=Hotels' },
      ]
    },
    { id: 'orders', label: 'Orders', icon: ShoppingBag, href: '/OrderListing' },
     { id: 'merchantdashboard', label: 'Merchant Dashboard', icon: ShoppingBag, href: '/MerchantDashboard' },
     { id: 'Provincial Council', label: 'Provincial Council', icon: ShoppingBag, href: '/ProvincialAdminDashboard' },
    { id: 'users', label: 'Users', icon: Users, href: '/UserManagementInterface' },
    { id: 'tour-guides', label: 'Tour Guides', icon: UserCheck, href: '/TourGuidesManagement' },
    { id: 'reports', label: 'Reports', icon: FileText, href: '/reports' },
    { id: 'settings', label: 'Settings', icon: Settings, href: '/Settings' },
    { id: 'cms', label: 'Cms', icon: Menu, href: '/BlockList' }
  ];

  const findParentMenu = (itemId) => {
    for (const item of menuItems) {
      if (item.subItems && item.subItems.some(sub => sub.id === itemId)) {
        return item.id;
      }
    }
    return null;
  };

  const persistSidebarState = (activePageId, openDropdownId) => {
    const sidebarState = {
      activePage: activePageId,
      openDropdown: openDropdownId
    };
    sessionStorage.setItem('sidebarState', JSON.stringify(sidebarState));
  };

  const restoreSidebarState = () => {
    try {
      const savedState = sessionStorage.getItem('sidebarState');
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (error) {
      console.warn('Failed to restore sidebar state:', error);
    }
    return null;
  };

  const handleNavigation = (itemId, href) => {
    setActivePage(itemId);
    setIsMobileMenuOpen(false);

    const parentMenu = findParentMenu(itemId);

    if (parentMenu) {
      setOpenDropdown(parentMenu);
      persistSidebarState(itemId, parentMenu);
    } else {
      const clickedItem = menuItems.find(item => item.id === itemId);
      if (clickedItem && clickedItem.subItems) {
        const newDropdownState = openDropdown === itemId ? null : itemId;
        setOpenDropdown(newDropdownState);
        persistSidebarState(itemId, newDropdownState);
      } else {
        setOpenDropdown(null);
        persistSidebarState(itemId, null);
      }
    }

    if (href) {
      navigate(href); // ✅ React Router handles route change
    }
  };

  const toggleDropdown = (itemId) => {
    const newDropdownState = openDropdown === itemId ? null : itemId;
    setOpenDropdown(newDropdownState);
    persistSidebarState(activePage, newDropdownState);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.sidebar-mobile') && !event.target.closest('.mobile-menu-button')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handlePopState = () => {
      const savedState = restoreSidebarState();
      if (savedState) {
        setActivePage(savedState.activePage);
        setOpenDropdown(savedState.openDropdown);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const savedState = restoreSidebarState();

    if (savedState && savedState.activePage) {
      setActivePage(savedState.activePage);
      setOpenDropdown(savedState.openDropdown);
    } else if (initialActivePage) {
      setActivePage(initialActivePage);
      const parentMenu = findParentMenu(initialActivePage);
      if (parentMenu) {
        setOpenDropdown(parentMenu);
        persistSidebarState(initialActivePage, parentMenu);
      } else {
        persistSidebarState(initialActivePage, null);
      }
    }
  }, [initialActivePage]);

  useEffect(() => {
    const handleExternalNavigation = (event) => {
      const { itemId } = event.detail;
      if (itemId) {
        setActivePage(itemId);
        const parentMenu = findParentMenu(itemId);
        if (parentMenu) {
          setOpenDropdown(parentMenu);
          persistSidebarState(itemId, parentMenu);
        }
      }
    };

    window.addEventListener('externalNavigation', handleExternalNavigation);
    return () => window.removeEventListener('externalNavigation', handleExternalNavigation);
  }, []);

  const SidebarContent = () => (
    <>
      <div className="sidebar-logo-area">
        <img src="/assets/img/logo.png" alt="Company Logo" className="sidebar-logo" />
        <button className="sidebar-close-button hidden-desktop" onClick={() => setIsMobileMenuOpen(false)}>
          <X size={24} />
        </button>
      </div>

      <div className="sidebar-menu-container">
        {menuItems.map((item) => {
          const isActive = activePage === item.id;
          const Icon = item.icon;

          if (item.subItems) {
            return (
              <div key={item.id}>
                <div
                  className={`menu-item-dropdown ${openDropdown === item.id ? 'open' : ''}`}
                  onClick={() => toggleDropdown(item.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div className="menu-item-icon-container">
                      <Icon size={18} className="menu-item-icon" />
                    </div>
                    <span className="menu-item-label">{item.label}</span>
                  </div>
                  {openDropdown === item.id ? (
                    <ChevronDown size={16} className="menu-item-icon" />
                  ) : (
                    <ChevronRight size={16} className="menu-item-icon" />
                  )}
                </div>
                {openDropdown === item.id && (
                  <div className="dropdown-content">
                    {item.subItems.map((sub) => (
                      <div
                        key={sub.id}
                        className={`dropdown-item submenu-item ${activePage === sub.id ? 'active' : ''}`}
                        onClick={() => handleNavigation(sub.id, sub.href)}
                      >
                        <div className="dropdown-item-dot"></div>
                        <span className="dropdown-item-label">
                          {sub.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <div
              key={item.id}
              className={`menu-item ${isActive ? 'active' : ''}`}
              onClick={() => handleNavigation(item.id, item.href)}
            >
              <div className="menu-item-icon-container">
                <Icon size={18} className="menu-item-icon" />
              </div>
              <span className="menu-item-label">{item.label}</span>
            </div>
          );
        })}
      </div>

      <div className="user-profile-section">
        <div className="user-avatar">
          <span className="user-avatar-text">KA</span>
        </div>
        <div className="user-info">
          <div className="user-name">K. Anderson</div>
          <div className="user-role">Administrator</div>
        </div>
        <button className="user-settings-button">
          <Settings size={16} className="menu-item-icon" />
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <div className="hidden-desktop">
        <button 
          className="mobile-menu-button" 
          onClick={() => setIsMobileMenuOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={24} />
        </button>
      </div>

      {/* Desktop Sidebar */}
      <div className="sidebar-desktop hidden-mobile">
        <SidebarContent />
      </div>

      {/* Mobile Sidebar */}
      {isMobileMenuOpen && (
        <>
          <div className="mobile-overlay" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="sidebar-mobile">
            <SidebarContent />
          </div>
        </>
      )}
    </>
  );
};

export default Sidebar;